import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';
import { LocalizedMessage } from 'core/l10n/components';
import React, { forwardRef, useState, useEffect } from 'react';
import DatePicker, { registerLocale, setDefaultLocale, getDefaultLocale } from 'react-datepicker';
import { InputGroup, InputGroupText } from 'reactstrap';
import { DEFAULT_DATE_FORMAT, ISO_UTC_FORMAT_DATE_FNS } from 'core/config/global';
import FAIcon from 'core/ovations-components/FAIcon';
import classNames from 'classnames';
import * as Popper from '@popperjs/core';
import { DEFAULT_LOCALE, STORAGE_KEY, SupportedLocale } from 'core/l10n/locales';
import us from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import fr from 'date-fns/locale/fr';
import frCa from 'date-fns/locale/fr-CA';
import { isAfter, isBefore, isEqual, format, subHours } from 'date-fns';

registerLocale(SupportedLocale.enUS, us);
registerLocale(SupportedLocale.esUS, es);
registerLocale(SupportedLocale.frFR, fr);
registerLocale(SupportedLocale.frCA, frCa);

export interface DateRange {
    rangeStart: string | null;
    rangeEnd: string | null;
}
export interface DateTimePickerProps {
    selectedDate: string | null;
    onChange: (dateTime: string | DateRange | null) => void;
    id?: string;
    showTimeSelect?: boolean;
    // timezone?: string;
    disabled?: boolean;
    required?: boolean;
    minDate?: string | null;
    maxDate?: string | null;
    invalidFeedback?: JSX.Element | string;
    placeholderText?: string;
    dateFormat?: string;
    className?: string;
    disableErrorMsg?: boolean;
    selectsRange?: boolean;
    endDate?: string | null;
    popperPlacement?: Popper.Placement | undefined;
}

type InputProps = JSX.IntrinsicElements['input'];

const CustomDateTimeInput = forwardRef<HTMLInputElement, InputProps>((props, ref) => (
    <InputGroup>
        <InputGroupText>
            <FAIcon icon={faCalendarAlt} />
        </InputGroupText>
        <input key="customDatePickerInput" ref={ref} {...props} />
    </InputGroup>
));

const MAX_TIMESTAMP = 8640000000000000;

export const DateTimePicker: React.FC<DateTimePickerProps> = (props) => {
    const isDST = (date: Date | null) => {
        if (date) {
            const jan = new Date(date.getFullYear(), 0, 1);
            const jul = new Date(date.getFullYear(), 6, 1);
            const offSet = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
            // DST VALUE
            if (date.getTimezoneOffset() < offSet) {
                return true;
            }
            return false;
        }
        return false;
    };

    const adjustDateForTimezone = (date: Date | null) => {
        // Setting to CST to UTC (+5) minus the timezone offset.
        let offset = 5;
        if (!isDST(date)) {
            offset = 6;
        }
        const tzOffset = date ? offset - date.getTimezoneOffset() / 60 : offset;
        return date ? subHours(date, tzOffset) : null;
    };

    const [selectedDate, setSelectedDate] = useState(props.selectedDate ? new Date(props.selectedDate) : null);
    const [endDate, setEndDate] = useState(props.endDate ? new Date(props.endDate) : null);
    const [isDateValid, setIsDateValid] = useState(true);
    const [locale, setLocale] = useState(getDefaultLocale());

    useEffect(() => {
        const locale = sessionStorage.getItem(STORAGE_KEY.OVERRIDE) || DEFAULT_LOCALE;
        setDefaultLocale(locale);
        setLocale(locale);
    }, []);

    useEffect(() => {
        const isRequiredValid = (value: string | null, required: boolean | undefined) => {
            let isValueValid = false;
            isValueValid = !required || !!value;
            return isValueValid;
        };

        const validateMinMaxDateTimes = (value: string | null) => {
            if (!value) {
                return true;
            }
            const date = new Date(value);

            const min = props.minDate ? new Date(new Date(props.minDate).setHours(0)) : new Date(MAX_TIMESTAMP * -1);
            const max = props.maxDate ? new Date(props.maxDate) : new Date(MAX_TIMESTAMP);

            const isValidMin = isAfter(date, min) || isEqual(date, min);
            const isValidMax = isBefore(date, max) || isEqual(date, max);
            return isValidMin && isValidMax;
        };

        setIsDateValid(
            isRequiredValid(props.selectedDate, props.required) && validateMinMaxDateTimes(props.selectedDate),
        );
    }, [props.selectedDate, props.required, props.minDate, props.maxDate]);

    const getDateFormat = () => {
        let dateFormat = 'P'; // long localized date format
        if (props.showTimeSelect) {
            dateFormat = `${dateFormat} p`; // long localized time format
        }
        return dateFormat;
    };

    function formatDateToCstIso(date: Date) {
        // Setting to CST to UTC (+5) minus the timezone offset.
        const utcOffsetHrs = 5 * 60 - date.getTimezoneOffset();
        const tzOffset = date.getTimezoneOffset();
        const d = new Date(date.valueOf() + (utcOffsetHrs + tzOffset) * 60 * 1000);

        // This accounts for Daylight Savings Time.
        // If the day you're picking is a DST day and between 12 and 1 am, we need to add
        // or subtract an hour so the conversion from UTC to CT accounts for that
        const newTzOffset = d.getTimezoneOffset();
        const newUtcOffsetHours = 5 * 60 - d.getTimezoneOffset();
        let tzOffsetDiff = newTzOffset - tzOffset;

        if (!isDST(date)) {
            tzOffsetDiff += 60;
        }

        const adjustedDateTime = new Date(
            date.valueOf() + (newUtcOffsetHours + newTzOffset + tzOffsetDiff) * 60 * 1000,
        );

        return format(adjustedDateTime, ISO_UTC_FORMAT_DATE_FNS);
    }

    const convertDateTime = (date: Date | null) => {
        if (date) {
            if (!props.showTimeSelect) {
                // no need for time unless we're using time picker
                return new Date(
                    formatDateToCstIso(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)),
                );
            }
            // using time picker, default everything to central
            return new Date(formatDateToCstIso(date));
        }
        return null;
    };

    const onDateTimeChangeHandler = (date: Date | Date[]) => {
        if (props.selectsRange) {
            // for date-range selection
            let utcDateStart: Date | null = null;
            let utcDateEnd: Date | null = null;
            const [start, end] = date as Date[];

            if (date) {
                if (start) utcDateStart = convertDateTime(start as Date);
                if (end) utcDateEnd = convertDateTime(end as Date);
            }
            setSelectedDate(utcDateStart);
            setEndDate(utcDateEnd);

            if ((start && end) || (!start && !end)) {
                props.onChange({
                    rangeStart: utcDateStart ? utcDateStart.toISOString() : null,
                    rangeEnd: utcDateEnd ? utcDateEnd.toISOString() : null,
                });
            }
        } else {
            // for date/time selection only
            const utcSelectedDate = convertDateTime(date as Date);
            setSelectedDate(utcSelectedDate);
            props.onChange(utcSelectedDate ? utcSelectedDate.toISOString() : null);
        }
    };

    const adjustDateForDST = (date: Date) => {
        // DST = 5, otherwise 6 for CST
        // Have to adjust our min and max range dates to match the current timezone
        const currentDate = new Date();
        const dateHours = date.getUTCHours();
        const dateMinutes = date.getUTCMinutes();

        if (isDST(currentDate) && dateHours === 6 && dateMinutes === 0) {
            date.setHours(date.getHours() - 1);
        } else if (!isDST(currentDate) && dateHours === 5 && dateMinutes === 0) {
            date.setHours(date.getHours() + 1);
        }
        return date;
    };

    return (
        <div className={classNames({ 'force-invalid is-invalid': !isDateValid })}>
            <DatePicker
                key={`datepicker-${props.id ?? 'onlyOne'}`}
                id={props.id}
                className={classNames('form-control', props.className)}
                onChange={(date: Date) => onDateTimeChangeHandler(date)}
                selected={adjustDateForTimezone(selectedDate)}
                minDate={props.minDate ? adjustDateForDST(new Date(props.minDate)) : null}
                maxDate={props.maxDate ? adjustDateForDST(new Date(props.maxDate)) : null}
                startDate={props.selectsRange ? adjustDateForTimezone(selectedDate) : undefined}
                endDate={props.selectsRange ? adjustDateForTimezone(endDate) : undefined}
                showTimeSelect={props.showTimeSelect}
                timeFormat="hh:mm aa"
                timeIntervals={30}
                dateFormat={getDateFormat()}
                disabled={props.disabled}
                placeholderText={props.placeholderText}
                customInput={
                    <CustomDateTimeInput id={props.id} className={classNames({ 'force-invalid': !isDateValid })} />
                }
                selectsRange={props.selectsRange}
                required={props.required}
                popperPlacement={props.popperPlacement}
                locale={locale}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
            />
            {!isDateValid && (
                <div className="invalid-feedback">
                    {props.invalidFeedback || (
                        <LocalizedMessage
                            id="generic_dateFormat_message"
                            values={{
                                format: props.dateFormat || DEFAULT_DATE_FORMAT,
                            }}
                        />
                    )}
                    <input id="datetimepicker_forced_validation" required className="invisible force-invalid" />
                </div>
            )}
        </div>
    );
};
